import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { yupResolver } from "@hookform/resolvers/yup";
import { useContext, useState } from "react";
import { useForm } from "react-hook-form";
import { object } from "yup";
import { CheckoutContext } from "../../../Checkout/CheckoutContext";
import { ModalContent, useModal } from "../../../Shared/components/Modal";
import { LogLevel } from "../../../Shared/hooks/useLogging/Logging-types";
import { useLogging } from "../../../Shared/hooks/useLogging/useLogging";
import useToast from "../../../hooks/useToast";
import { useOrderFlow } from "../../OrderFlowProvider/OrderFlowContext";
import { OrderFlowErrorMessages, useSession } from "../../hooks/Session/useSession";
import { couponFormSchema, CouponText } from "./CouponForm-types";
import { Button, DialogActions } from "@mui/material";
import { AdjustmentValidation } from "../../../MealTrack/MTAPITypes";
/**
 * Hook to control coupon form
 *
 * @param paymentUpdateCallback - Call back for updating the payment intent
 * @returns
 */
export const useCouponForm = (paymentUpdateCallback) => {
    const [loading, setLoading] = useState(false);
    const { SaveOrderFlowSession } = useSession();
    // TODO REPLACE AND REMOVE ALL INSTANCES OF AND REFERENCES TO pricingBreakdown WITH EQUIVALENT FROM BALANCE OBJECT
    const { store, setPricingBreakdown, setBalance } = useOrderFlow();
    const toast = useToast();
    const { datadogLog } = useLogging();
    const { showModal, closeModal } = useModal();
    const { setOrderInfo } = useContext(CheckoutContext);
    const { handleSubmit, control, setError, formState: { errors }, clearErrors, resetField, } = useForm({
        mode: "onChange",
        resolver: yupResolver(object(couponFormSchema)),
    });
    const onSubmit = handleSubmit((data) => {
        const couponCode = data.couponCode;
        setLoading(true);
        SaveOrderFlowSession(store.cart, store.model, false, true, false, couponCode)
            .then((res) => handleCouponResponse(res, couponCode))
            .catch(handleCouponError);
    });
    // Handles the API response
    const handleCouponResponse = (res, couponCode) => {
        var _a;
        if (!((_a = res === null || res === void 0 ? void 0 : res.data) === null || _a === void 0 ? void 0 : _a.success)) {
            showToastError(OrderFlowErrorMessages.COUPON);
            setLoading(false);
            return;
        }
        const { pricingBreakdown, balance, orderInfo } = res.data;
        setPricingBreakdown(pricingBreakdown);
        setBalance(balance);
        setOrderInfo(orderInfo);
        handleDiscountValidation(balance.cost.order.adjustments, couponCode);
        updatePaymentIntent(balance);
    };
    // Handles discount validation and modal display
    const handleDiscountValidation = (adjustments, couponCode) => {
        if (adjustments.length === 0)
            return;
        const targetCoupon = adjustments.find((adjustment) => adjustment.name.toLowerCase() === couponCode.toLowerCase());
        if (!targetCoupon)
            return;
        if (targetCoupon.validation !== AdjustmentValidation.Valid) {
            showToastError(`${targetCoupon.validation} coupon code`);
            setLoading(false);
            return;
        }
        if (hasTerms(targetCoupon.terms)) {
            showTermsModal(targetCoupon.terms, couponCode);
        }
        else {
            applyCouponSuccess(couponCode);
        }
    };
    // Determines if terms are valid
    const hasTerms = (terms) => (terms === null || terms === void 0 ? void 0 : terms.trim().length) > 0;
    // Displays the terms modal
    const showTermsModal = (terms, couponCode) => {
        showModal({
            hideCloseButton: true,
            fullWidth: true,
            title: CouponText.TERMS_MODAL_HEADER,
            content: _jsx(ModalContent, { children: terms }),
            actions: (_jsxs(DialogActions, Object.assign({ style: { borderTop: "1px solid #DEDDDE" } }, { children: [_jsx(Button, Object.assign({ size: "medium", variant: "outlined", color: "secondary", onClick: () => handleTermsCancel(couponCode) }, { children: CouponText.CANCEL })), _jsx(Button, Object.assign({ size: "medium", color: "primary", variant: "contained", onClick: () => applyCouponSuccess(couponCode) }, { children: CouponText.CONFIRM }))] }))),
            closeModalFunc: () => setLoading(false),
        });
    };
    // Removes a coupon
    const handleTermsCancel = (couponCode) => {
        SaveOrderFlowSession(store.cart, store.model, false, true, true, couponCode)
            .then((res) => {
            var _a;
            if ((_a = res === null || res === void 0 ? void 0 : res.data) === null || _a === void 0 ? void 0 : _a.success) {
                const { pricingBreakdown, balance, orderInfo } = res.data;
                setPricingBreakdown(pricingBreakdown);
                setBalance(balance);
                setOrderInfo(orderInfo);
                toast.push(`${CouponText.TERMS_MODAL_CANCEL}: ${couponCode}`, { variant: "warning" });
                closeModal();
                clearErrors("couponCode");
                resetField("couponCode");
                setLoading(false);
            }
        });
    };
    // Handles coupon application success
    const applyCouponSuccess = (couponCode) => {
        toast.push(`${CouponText.COUPON_APPLIED}: ${couponCode}`, { variant: "success" });
        closeModal();
        clearErrors("couponCode");
        resetField("couponCode");
        setLoading(false);
    };
    // Updates payment intent if applicable
    const updatePaymentIntent = (balance) => {
        if (paymentUpdateCallback && balance) {
            paymentUpdateCallback(balance.remainingBalance);
        }
    };
    // Handles API errors
    const handleCouponError = (err) => {
        showToastError(OrderFlowErrorMessages.COUPON);
        datadogLog(`Caught exception in ApplyCoupon submit. Message: ${err.message}`, LogLevel.ERROR, undefined, err);
        setLoading(false);
    };
    // Utility for showing toast errors
    const showToastError = (message) => {
        toast.push(message, { variant: "error" });
    };
    return {
        onSubmit,
        showModal,
        closeModal,
        loading,
        control,
        errors,
    };
};
